import { useCallback } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb, BreadcrumbItem, Button } from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { pageTitle } from 'utils/page-title';
import { SelectEvaluationsStep } from './select-evaluations-step';
import { CurrentSelections } from './current-selections';
import { selectedEvalsAtom } from './atoms';
import { useOriginAutopopulate } from './hooks';
import { buildCompareUrl } from './utils';
import type { EvalUnifiedResult } from './types';
import style from './wizard.module.scss';

/**
 * Route entry point for the Compare Evaluations page.
 * Single page: a paginated evaluation table (left) plus a live Current
 * Selections sidebar (right). Picking two evaluations enables Compare, which
 * hard-navigates to the comparison results page. The first pick is the Origin
 * (baseline); the second is the Target it is compared against.
 * @return {JSX.Element} The compare evaluations page
 */
export function CompareEvaluationsPage() {
  const history = useHistory();
  const selectedEvals = useAtomValue(selectedEvalsAtom);
  const setSelectedEvals = useSetAtom(selectedEvalsAtom);
  const canCompare = selectedEvals.length === 2;

  // Pre-fill the Origin when launched for a specific eval (e.g. a package's
  // Actions → "Package Compare"), via the ?side_a_* params. No-op otherwise.
  useOriginAutopopulate();

  // Remove one picked evaluation from the sidebar. Because the table's checkbox
  // selection is controlled by this same atom, clearing it here also unchecks
  // the corresponding row.
  const handleRemoveSelection = useCallback(
    (evaluation: EvalUnifiedResult) => {
      setSelectedEvals((prev) =>
        prev.filter(
          (e) =>
            !(e.evalId === evaluation.evalId && e.source === evaluation.source)
        )
      );
    },
    [setSelectedEvals]
  );

  const handleCancel = useCallback(() => {
    window.close();
    // Fallback if window.close() is blocked (not opened via script)
    history.push('/packages');
  }, [history]);

  const handleCompare = useCallback(() => {
    if (selectedEvals.length !== 2) return;
    // Full-page navigation is required, not a stylistic choice: buildCompareUrl
    // targets routes.compareResultPath, a separate Rails-rendered entry point with
    // its own React root — NOT a sibling <Route> in this wizard's SPA router — so
    // history.push would change the URL without mounting the results page. This
    // also preserves the prior wizard's external-finish (own-tab) behavior now
    // that the Summary step is gone.
    window.location.href = buildCompareUrl(selectedEvals[0], selectedEvals[1]);
  }, [selectedEvals]);

  return (
    <>
      <Helmet>
        <title>{pageTitle('Compare Evaluations')}</title>
      </Helmet>
      <div className={style.page}>
        <header className={style.header}>
          <div className={style.breadcrumbs}>
            <Breadcrumb noTrailingSlash>
              <BreadcrumbItem>
                <NavLink to="/packages">Packages</NavLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>Compare Evaluations</BreadcrumbItem>
            </Breadcrumb>
          </div>
          <h1 className={style.title}>Compare Evaluations</h1>
        </header>

        <div className={style.body}>
          <div className={style.mainColumn}>
            <SelectEvaluationsStep />
          </div>
          <div className={style.sidebar}>
            <CurrentSelections
              selections={selectedEvals}
              onRemove={handleRemoveSelection}
            />
          </div>
        </div>

        <footer className={style.footer}>
          <Button
            className={style.cancelButton}
            kind="ghost"
            size="xl"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className={style.compareButton}
            kind="primary"
            size="xl"
            disabled={!canCompare}
            onClick={handleCompare}
          >
            Compare
          </Button>
        </footer>
      </div>
    </>
  );
}
